import type {ChatOwnerControl} from '../task/environment-strip/chat-owner-control'
import {PENDING_SSO_ORG_STORAGE_KEY} from '../task/pending-sso-org'
import {PENDING_SSO_TARGET_STORAGE_KEY} from '../task/pending-sso-target'
import type {AvailableOrganization, TaskComposerScope} from '../task/shared'

interface SplitPickerAvailability {
  flagEnabled: boolean
  isSandboxDraft: boolean
  selectedEnvironmentKind?: string
  canSelectChat: boolean
  currentUserLogin?: string
  hasOwnerChoice: boolean
  hasOwnerPanel: boolean
}

export function isOwnerSplitPickerActive({
  flagEnabled,
  isSandboxDraft,
  selectedEnvironmentKind,
  canSelectChat,
  currentUserLogin,
  hasOwnerChoice,
  hasOwnerPanel,
}: SplitPickerAvailability): boolean {
  return (
    flagEnabled &&
    isSandboxDraft &&
    selectedEnvironmentKind === 'sandbox' &&
    canSelectChat &&
    Boolean(currentUserLogin) &&
    (hasOwnerPanel || !hasOwnerChoice)
  )
}

export function buildChatOwnerPresentation({
  selectedScope,
  retainedOwnerLogin,
  currentUserLogin,
  currentUserAvatarUrl,
}: {
  selectedScope: TaskComposerScope
  retainedOwnerLogin?: string
  currentUserLogin: string
  currentUserAvatarUrl?: string
}): Pick<ChatOwnerControl, 'label' | 'accessibleLabel' | 'avatarUrl' | 'kind'> {
  const organization = selectedScope.kind === 'organization' ? selectedScope.organization : undefined
  return {
    label: retainedOwnerLogin ?? organization?.login ?? 'Personal',
    accessibleLabel: retainedOwnerLogin ?? organization?.login ?? `Personal (${currentUserLogin})`,
    avatarUrl: retainedOwnerLogin ? undefined : organization ? organization.avatarUrl : currentUserAvatarUrl,
    kind: retainedOwnerLogin || organization ? 'organization' : 'user',
  }
}

export function readPendingSsoChatTarget(storage?: Pick<Storage, 'getItem'>): {
  present: boolean
  pendingOrgLogin: string | null
} {
  try {
    const session = storage ?? sessionStorage
    return {
      present: session.getItem(PENDING_SSO_TARGET_STORAGE_KEY) === 'chat',
      pendingOrgLogin: session.getItem(PENDING_SSO_ORG_STORAGE_KEY),
    }
  } catch {
    return {present: false, pendingOrgLogin: null}
  }
}

export function getPendingSsoChatTargetSnapshot(): string | null {
  const pending = readPendingSsoChatTarget()
  return pending.present ? (pending.pendingOrgLogin ?? '') : null
}

export function getPendingSsoChatTargetServerSnapshot(): null {
  return null
}

export function subscribeToPendingSsoChatTarget(): () => void {
  return () => {}
}

export function resolvePendingSsoChatReturn({
  pending,
  organizations,
}: {
  pending: ReturnType<typeof readPendingSsoChatTarget>
  organizations: AvailableOrganization[]
}): boolean {
  return Boolean(
    pending.present &&
    pending.pendingOrgLogin &&
    organizations.some(
      organization =>
        organization.login.toLowerCase() === pending.pendingOrgLogin?.toLowerCase() &&
        organization.requiresSso !== true,
    ),
  )
}
